import { motion } from 'framer-motion';

export function Big3Guide() {
  return (
    <div className="pb-20">
      <div className="relative h-[40vh] overflow-hidden">
        <div className="absolute inset-0 bg-[#0B0E14]/70 z-10" />
        <img 
          src="https://static.lumi.new/88/88ab7525a467f0539d825e62a6507b48.png" 
          alt="Astrology Big 3 Art" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="text-center px-4">
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-4">The Big 3 in Astrology</h1>
            <p className="text-xl text-[#D4AF37] font-medium">Sun • Moon • Rising</p>
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-30">
        <div className="bg-[#131722] rounded-2xl p-8 md:p-12 border border-white/5 shadow-xl">
          <div className="prose prose-invert prose-lg max-w-none">
            <p className="lead text-xl text-gray-300 leading-relaxed mb-8">
              "What are your Big 3?" has become the new "What's your sign?" To truly understand your astrological profile, you need to look beyond just your horoscope. You need to <strong>find sun moon and rising sign</strong> placements to see the full picture.
            </p>

            <h2 className="text-2xl font-serif text-white mt-12 mb-6">The Trinity of Self</h2>
            <p className="text-gray-400">
              Your personality is a complex tapestry woven from three primary threads. While your Sun sign describes your core essence, it is only one part of the story.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12 not-prose">
              <InfoCard 
                title="The Sun" 
                subtitle="Your Core Identity" 
                desc="The 'I am'. Your ego, basic personality, and life purpose. It's what you grow into."
                color="bg-orange-500/10 text-orange-400 border-orange-500/20"
              />
              <InfoCard 
                title="The Moon" 
                subtitle="Your Inner World" 
                desc="The 'I feel'. Your emotions, instincts, and subconscious. Who you are when you're alone."
                color="bg-blue-500/10 text-blue-400 border-blue-500/20"
              />
              <InfoCard 
                title="The Rising" 
                subtitle="Your Exterior" 
                desc="The 'I appear'. Your first impression, physical demeanor, and the lens through which you see the world."
                color="bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
              />
            </div>

            <h3 className="text-xl font-bold text-white mt-8">How to Find Sun Moon and Rising Sign</h3>
            <p className="text-gray-400">
              Finding your Big 3 requires more than just your birth date. Because the Rising sign (Ascendant) changes approximately every two hours, and the Moon moves about 13 degrees per day, accuracy is key.
            </p>
            <ol className="list-decimal pl-6 space-y-4 text-gray-400 marker:text-[#D4AF37]">
              <li><strong>Exact Birth Time:</strong> Locate your birth certificate. Even a 10-minute difference can change your Rising sign.</li>
              <li><strong>Birth Location:</strong> The coordinates of your birth place determine the angle of the ecliptic.</li>
              <li><strong>Time Zone Calculation:</strong> Our calculator automatically adjusts for Daylight Savings Time and historical time zone changes.</li>
            </ol>

            <h3 className="text-xl font-bold text-white mt-8">Why the Rising Sign is the "Key"</h3>
            <p className="text-gray-400">
              In ancient astrology, the Rising sign was simply called "The Horoscope" (from Greek <em>horoskopos</em>, "marker of the hour"). It sets up your entire chart. If you read horoscopes, you should actually read for your <strong>Rising Sign</strong>, not your Sun sign, as this will align the houses correctly for more accurate predictions.
            </p>

          </div>
        </div>
      </article>
    </div>
  );
}

function InfoCard({ title, subtitle, desc, color }: { title: string, subtitle: string, desc: string, color: string }) {
  return (
    <div className={`p-6 rounded-xl border ${color}`}>
      <h4 className="text-lg font-bold mb-1">{title}</h4>
      <div className="text-xs uppercase tracking-wider opacity-70 mb-3">{subtitle}</div>
      <p className="text-sm leading-relaxed opacity-90">{desc}</p>
    </div>
  );
}
